import { useState } from "react";
import { getTicketTypeName } from "~components/checkout/order/checkout.order.List";
import { useCheckoutContext } from "~context/checkoutContext";
import { LineItemType } from "~graphql/typed-document-nodes";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { formatSeatLocation } from "~lib/helpers/formatSeatLocation";
import useCallbackQueue from "~features/single-page-checkout/hooks/useCallbackQueue";
import { OrderSummaryContext } from "~features/single-page-checkout/components/OrderSummary/OrderSummaryContext";
import { useServiceCosts } from "~features/single-page-checkout/components/OrderSummary/hooks/useServiceCosts";
import { SinglePageCheckoutListItem } from "~features/single-page-checkout/components/OrderSummary/ListItem";
import { ExpressCheckoutSummary } from "./ExpressCheckoutSummary";

export function OrderSummaryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { order, mutate, totals } = useCheckoutContext();

  const { addToQueue } = useCallbackQueue({
    deduplicationStrategy: "keep-last",
    onCallbackFinished: (_, queue) => {
      if (queue.filter((item) => item.id !== "submit").length > 0) {
        setIsLoading(true);
      } else {
        void mutate().then(() => {
          setIsLoading(false);
        });
      }
    },
  });

  const serviceCosts = useServiceCosts();

  const startDate = order?.event?.startDate ?? order?.membership?.startDate;
  const endDate = order?.event?.endDate ?? order?.membership?.endDate;
  const timezone =
    order?.event?.venue?.timezone ?? order?.membership?.venue?.timezone;
  const locale =
    order?.event?.venue?.locale ?? order?.membership?.venue?.locale;

  const totalServiceCost = serviceCosts.reduce(
    (acc, { price }) => +(acc + (price ?? 0)).toFixed(2),
    0
  );

  return (
    <OrderSummaryContext.Provider
      value={{
        image: order?.event?.logo ?? order?.membership?.thumbnail,
        title: order?.event?.title ?? order?.membership?.name,
        subtitle: formatEventDateRange(startDate, endDate, {
          format: "basicLong",
          timeZone: timezone,
          locale,
        }),

        allowPromoCode: false,
        isPromoCodeApplied: false,

        renderLineItems: () => (
          <>
            {order?.lineItems?.edges?.map(({ node }) => (
              <SinglePageCheckoutListItem
                key={node.id}
                title={`${node.quantity} x ${getTicketTypeName(
                  LineItemType.Addon,
                  node.name
                )}`}
                subtitle={formatSeatLocation(
                  node.seatZone,
                  node.seatSection,
                  node.seatLabel
                )}
                price={node.price * node.quantity}
              />
            ))}
          </>
        ),
        renderServiceCosts: () => (
          <>
            {serviceCosts.map(({ price, title, subtitle, renderPrice }) => (
              <SinglePageCheckoutListItem
                key={title}
                title={title}
                subtitle={subtitle}
                price={price}
                renderPrice={renderPrice}
                isLoading={isLoading}
              />
            ))}
          </>
        ),
        renderTotals: () => (
          <ExpressCheckoutSummary totalToBePaid={totals?.remainingPayment} />
        ),
        totalServiceCost,

        // Single page specific actions
        isLoading,
        setLoading: (setLoading: boolean) => {
          setIsLoading(setLoading);
        },
        addToQueue: (id: string, callback: () => Promise<void>) => {
          // The loading state is just for the orderSummary component so if its the submit button
          // we don't want to set it to loading.
          if (id !== "submit") {
            setIsLoading(true);
          }
          addToQueue(id, callback);
        },
      }}
    >
      {children}
    </OrderSummaryContext.Provider>
  );
}
